import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useSeo } from "../lib/useSeo";
import { Button } from "../components/ui/button";
import EmployerLogo from "../components/EmployerLogo";
import {
  ArrowRight,
  CheckCircle2,
  ArrowUpRight,
} from "lucide-react";
import OperatorSubscribeWidget from "../components/OperatorSubscribeWidget";

const OPERATORS = [
  "Avanti West Coast", "LNER", "GWR", "ScotRail", "Northern", "TransPennine Express",
  "CrossCountry", "East Midlands Railway", "Greater Anglia", "Southeastern", "SWR", "c2c",
  "Govia Thameslink Railway", "Chiltern", "Transport for Wales", "Caledonian Sleeper",
  "Heathrow Express", "Hull Trains", "Lumo", "Grand Central", "Merseyrail",
  "DB Cargo UK", "Freightliner", "GB Railfreight", "Direct Rail Services", "Network Rail",
];

const SITE_URL = "https://railwaycareers.co.uk";

const SECTIONS = [
  {
    id: "overview",
    h: "What does a UK train driver do?",
    p: [
      "A UK train driver operates passenger or freight trains safely across the national rail network, in line with signals, speed limits, route knowledge and operational rules. The role is safety-critical: every decision a driver makes — from braking on the approach to a station to responding to a signal failure — directly affects the safety of passengers, colleagues and members of the public.",
      "Day-to-day work involves preparing the train at the depot, checking brakes and traction equipment, operating the controls in service, handling station calls, communicating with signallers and control, and dealing with disruption when it occurs. Drivers work alone in the cab and must remain alert across long shifts, including early mornings, late nights, weekends and bank holidays.",
      "Operators in the UK include passenger Train Operating Companies (TOCs) like LNER, Avanti West Coast, GWR, ScotRail, Northern, Greater Anglia, Southeastern, GTR, SWR, TPE, c2c, CrossCountry, East Midlands Railway, Transport for Wales, Chiltern, Caledonian Sleeper, Heathrow Express, Hull Trains, Lumo, Grand Central, Merseyrail and London Overground; freight operators including DB Cargo UK, Freightliner, GB Railfreight, Direct Rail Services, Colas Rail and Rail Operations Group; and infrastructure / charter / heritage operators.",
    ],
  },
  {
    id: "salary",
    h: "How much do UK train drivers earn?",
    p: [
      "Trainee train driver salaries typically range from £27,000 to £33,000, depending on the operator and depot location. Once qualified, UK train drivers usually earn between £48,000 and £68,000 in basic pay — plus shift premiums, weekend allowances, mileage payments and overtime, which can lift total earnings significantly.",
      "Pay varies by operator, route type (passenger or freight), traction (diesel, electric, multi-mode) and length of service. Long-distance, high-speed and freight roles typically sit at the upper end of the band, while shorter-route, suburban and turn-up-and-go services tend to sit toward the middle. Some London and South East operators include a London weighting on top.",
      "Train driver pay is set through collective agreements with the recognised trade unions. Annual pay deals are negotiated nationally or per-operator, and most agreements include rest-day working rates, Sunday premiums and bank-holiday enhancements.",
    ],
    facts: [
      { k: "Trainee", v: "£27k – £33k" },
      { k: "Qualified", v: "£48k – £68k" },
      { k: "Plus", v: "Shift premiums, OT, mileage" },
      { k: "Pension", v: "Final-salary or DB schemes" },
    ],
  },
  {
    id: "how-to-become",
    h: "How do you become a train driver in the UK?",
    p: [
      "There is no single qualification or college course that turns you into a UK train driver. The route is always: apply to a Train Operating Company or Freight Operating Company when they advertise a trainee train driver vacancy, pass their selection process, and complete their structured training programme. Training and competence are managed under industry standards, including RIS-3751-TOM for psychometric assessments and railway safety competence requirements.",
      "Trainee driver roles are usually advertised in cohorts. Some operators run rolling recruitment; others open windows once or twice a year. Competition is strong — a single trainee vacancy at a busy depot can attract several thousand applications. Strong applicants understand shift work, demonstrate safety-focused thinking, and show that they have prepared for the assessments rather than relying on raw aptitude.",
      "If you're still researching the process, start with the free guides and tools on our [Resources](/resources) tab. For more structured preparation, Train Driver Foundation also publishes detailed guidance on [how to become a train driver](https://www.traindriverfoundation.com/become-a-driver), including application, CV, interview and assessment support.",
    ],
    steps: [
      { n: "01", h: "Apply", p: "Find a live trainee train driver vacancy with the operator and depot you can realistically commute to. Apply through the operator's official careers page." },
      { n: "02", h: "Screening", p: "The operator screens applications against essential criteria: right to work, age, location, shift availability, previous safety-critical experience and motivation." },
      { n: "03", h: "Psychometric assessments", p: "Online or assessment-centre tests covering concentration, attention, reaction time, working memory, rule-based decision making, communication and consistency under pressure." },
      { n: "04", h: "Structured interview", p: "Competency-based interview around safety, motivation, behavioural examples, shift work, dealing with disruption and rule compliance." },
      { n: "05", h: "Assessment centre", p: "Some operators run a face-to-face assessment day with group exercises, role-play, scenario interviews and follow-up tests." },
      { n: "06", h: "Conditional offer + medical", p: "Safety-critical medical, references, DBS where applicable, employment history checks and drug and alcohol screening." },
      { n: "07", h: "Training programme", p: "Classroom rules training, traction training, route learning and supervised practical driving. Typically 9–14 months, but varies. You're paid throughout." },
      { n: "08", h: "Pass-out and qualified driving", p: "Final assessments, sign-off by a competent assessor, then you drive in service. Ongoing route learning, refreshers and assessments continue throughout your career." },
    ],
  },
  {
    id: "eligibility",
    h: "Train driver eligibility — am I eligible?",
    list: [
      "**Right to work** in the UK without restriction.",
      "**Age**: typically 21+ for mainline driver roles. This is under review and is likely to be lowered to 18.",
      "**Medical fitness**: ability to pass a safety-critical medical including eyesight, hearing and colour vision.",
      "**Drug & alcohol screening**: random and pre-employment testing applies.",
      "**Location**: most operators require trainees to live within a sensible commuting distance of the depot — typically 45-60 minutes (based on a 0830 travel time or similar). Use our [Vacancy Map](/depot-finder) to filter live driver vacancies by drive-time from your postcode.",
      "**Education**: most operators require GCSEs or equivalent including Maths and English.",
      "**Experience**: you do not need rail experience. Many trainees come from the armed forces, emergency services, transport, logistics, manufacturing, customer-facing operational roles or internal railway grades.",
      "**Background checks**: pre-employment checks include identity, references, employment history, address history and criminal record checks where applicable.",
    ],
  },
  {
    id: "psychometrics",
    h: "What are the train driver psychometric tests?",
    p: [
      "UK train driver psychometric assessments are standardised across most operators because the underlying competencies are defined by industry standards. The exact tests an operator uses can vary — some run them online, some at an assessment centre — but the underlying measures are similar.",
      "The tests measure sustained concentration, selective attention, reaction time, short-term and working memory, rule-based decision making, dual-tasking, mathematical reasoning under time pressure, and consistency. They are not IQ tests. They are not designed to be 'gameable.' But they are very much practiseable — most candidates who fail on aptitude do so because they underestimate the format and run out of time.",
      "For free starting points, check the [Resources](/resources) tab. Candidates who want dedicated practice can also use [Train Driver Psychometrics](https://traindriverpsychometrics.co.uk/), which provides train driver test practice built around the main assessment areas without claiming to copy live operator tests.",
    ],
    list: [
      "**TEA-Occ** — sustained attention with auditory distractors.",
      "**SCAAT / Group Bourdon style tests** — visual selective attention under time pressure.",
      "**TRP1/TRP2** — trainability tests based on learning and applying new rules.",
      "**WAFV / vigilance-style tests** — long, repetitive tasks measuring alertness.",
      "**Mechanical comprehension** — basic mechanical understanding where used by the operator.",
      "**Numerical reasoning** — percentages, ratios, time-distance and timed calculations.",
    ],
  },
  {
    id: "training",
    h: "What does train driver training look like?",
    p: [
      "Once you've passed selection and the medical, you start a paid training programme that typically runs 9–14 months. The exact mix of classroom, simulator, workshop and practical depends on the operator, traction type, route complexity and training capacity.",
      "Most programmes follow this rough structure: rules training, traction training, route learning, simulator practice, and supervised practical driving with a Driver Instructor. At pass-out, you drive in service alone, but ongoing refreshers, route revalidations and yearly assessments continue throughout your career.",
      "Before applying, it can also help to build wider railway knowledge. The free resources on our [Resources](/resources) tab are a good place to start, and Train Driver Foundation offers railway-focused [CPD courses](https://www.traindriverfoundation.com/cpd-courses) for candidates who want a more structured understanding of safety-critical railway work.",
    ],
  },
  {
    id: "shifts",
    h: "What are the working patterns?",
    p: [
      "Train driving is shift work. Most operators run a rolling roster covering 24/7 service, with early turns starting around 03:30–05:00, late turns finishing 23:00–01:30, and a mix of weekends and bank holidays.",
      "Drivers typically work a 35–37 hour week spread across 4–5 shifts, with statutory rest periods and rostered rest days. Rest-day working at premium rates is widely available at many operators.",
    ],
  },
  {
    id: "progression",
    h: "Career progression after qualifying",
    list: [
      "**Driver Instructor** — train new entrants.",
      "**Standards / Competence Manager** — operational safety, audit and investigations.",
      "**Driver Manager / Team Leader** — first-line management of a depot's driver complement.",
      "**Operations Manager** — train service control, route control and performance.",
      "**Driver Examiner / Assessor** — assess competence of drivers across the operator.",
      "**Training Centre roles** — classroom delivery, simulator instruction and programme design.",
      "**Movement to freight, charter, infrastructure or heritage railways** — more variety and different operating patterns.",
    ],
  },
  {
    id: "operators",
    h: "Which UK operators recruit train drivers?",
    p: [
      "Almost every UK passenger and freight operator recruits trainee train drivers at some point in the year. Browse our [Operators directory](/operators) for the full list with current vacancy counts, or use the links below to jump to common recruiters.",
    ],
  },
];

const FAQS = [
  {
    q: "How do I become a train driver in the UK with no experience?",
    a: "Apply directly to a Train Operating Company or Freight Operating Company when they advertise a trainee train driver vacancy. You do not need rail experience — most trainees come from outside the industry. The selection process usually includes application, psychometric assessments, structured interview, possible assessment centre, medical, then 9–14 months of paid training.",
  },
  {
    q: "What is the train driver salary in the UK?",
    a: "Trainee train drivers in the UK typically earn £27,000–£33,000 during training. Qualified train drivers typically earn £48,000–£68,000 in basic pay, with shift premiums, weekend allowances, rest-day working and mileage payments often pushing total earnings higher.",
  },
  {
    q: "How long does it take to become a train driver?",
    a: "Once you've been offered a trainee role, training usually takes 9 to 14 months including classroom rules training, traction training, route learning, simulator work and supervised practical driving. Getting the trainee role itself is usually the hard part because competition is strong.",
  },
  {
    q: "Can I become a train driver at 18?",
    a: "Most mainline UK Train Operating Companies require trainee drivers to be 21 or over. Some Tube, metro, tram and apprenticeship routes may take entrants from 18. Check each individual vacancy.",
  },
  {
    q: "Do I need GCSEs to become a train driver?",
    a: "Most operators ask for GCSEs or equivalent including Maths and English at grade C/4 or above. Some operators may consider equivalent experience or apprenticeship pathways.",
  },
  {
    q: "Is colour blindness a problem for train driver jobs?",
    a: "Severe colour blindness is normally disqualifying because of railway signalling requirements. The safety-critical medical includes colour-vision testing. Always check with the operator before assuming.",
  },
  {
    q: "Can I be a train driver if I wear glasses?",
    a: "Yes, provided your corrected vision meets the safety-critical medical standard. Glasses and contact lenses are widely accepted.",
  },
  {
    q: "How do I prepare for the train driver assessments?",
    a: "Understand the format of each test and practise under time pressure. Don't try to memorise questions — the tests are designed so that won't help. Instead, practise sustained attention, build stamina for long sessions, and rehearse safety-focused behavioural examples for the structured interview. Start with our [free resources](/resources), then consider structured practice through [Train Driver Psychometrics](https://traindriverpsychometrics.co.uk/) if you want a more focused preparation platform.",
  },
  {
    q: "Can a conductor become a train driver?",
    a: "Yes. Many UK train drivers progressed internally from conductor, station, dispatch or depot roles. Internal applicants must still pass the same assessments, medical and training, but operational experience often helps in the structured interview.",
  },
  {
    q: "How often do trainee train driver vacancies come up?",
    a: "Varies by operator and depot. Big operators may run trainee cohorts several times a year; quieter depots may go much longer between campaigns. Subscribe to the [Monday Briefing](/) to be notified when new trainee driver vacancies are posted.",
  },
];

function renderInlineMarkdown(text) {
  return text
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-ink font-semibold underline hover:text-amber-700">$1</a>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-ink font-semibold underline hover:text-amber-700">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-ink">$1</strong>');
}

export default function TrainDriverJobs() {
  const [vacancies, setVacancies] = useState([]);
  const [stats, setStats] = useState({ live_vacancies: null, employers: null });

  useEffect(() => {
    api.get("/vacancies?role=train-driver&limit=8")
      .then((r) => setVacancies(r.data || []))
      .catch(() => {});

    api.get("/stats")
      .then((r) => setStats(r.data || {}))
      .catch(() => {});
  }, []);

  useSeo({
    title: "Train Driver Jobs UK 2026 | Salary, Training & Live Vacancies",
    description:
      "Complete UK guide to train driver jobs — trainee and qualified driver salaries, psychometric assessments, training, eligibility, top operators, free resources and live vacancies.",
    canonical: "/train-driver-jobs",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Train Driver Jobs UK — Salary, Training, Recruitment & Live Vacancies",
      description:
        "Complete UK guide to becoming a train driver — trainee and qualified salaries, psychometric assessments, eligibility, training programmes, top operators, free resources and live vacancies.",
      author: { "@type": "Organization", name: "Railway Careers UK" },
      publisher: {
        "@type": "Organization",
        name: "Railway Careers UK",
        logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` },
      },
      datePublished: "2026-04-29",
      dateModified: new Date().toISOString().split("T")[0],
      mainEntityOfPage: `${SITE_URL}/train-driver-jobs`,
      about: [
        "Train driver jobs UK",
        "Trainee train driver",
        "How to become a train driver",
        "Train driver salary UK",
        "Train driver psychometric assessments",
        "Train driver training",
        "Train Operating Company driver recruitment",
      ],
      mentions: [
        { "@type": "WebSite", name: "Train Driver Foundation", url: "https://www.traindriverfoundation.com/" },
        { "@type": "WebSite", name: "Train Driver Psychometrics", url: "https://traindriverpsychometrics.co.uk/" },
      ],
    },
  });

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1"),
      },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Train Driver Jobs UK", item: `${SITE_URL}/train-driver-jobs` },
    ],
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How to become a train driver in the UK",
    description:
      "Step-by-step UK trainee train driver recruitment process: application, screening, psychometric assessments, interview, medical and training.",
    totalTime: "P12M",
    estimatedCost: { "@type": "MonetaryAmount", currency: "GBP", value: "0" },
    step: SECTIONS.find((s) => s.id === "how-to-become").steps.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.h,
      text: s.p,
    })),
  };

  return (
    <div className="bg-cream min-h-screen" data-testid="train-driver-jobs-page">
      <section className="bg-navy text-bone">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-20">
          <div className="text-xs uppercase tracking-[0.2em] text-amber font-semibold">
            UK Cornerstone Guide · Updated{" "}
            {new Date().toLocaleDateString("en-GB", {
              month: "long",
              year: "numeric",
            })}
          </div>

          <h1 className="font-display font-extrabold text-4xl md:text-6xl tracking-tight leading-[1.05] mt-3">
            Train Driver Jobs UK
          </h1>

          <p className="text-bone/80 mt-6 text-lg md:text-xl leading-relaxed max-w-3xl">
            Train driver jobs in the UK pay{" "}
            <strong className="text-amber">£27,000–£33,000 as a trainee</strong>{" "}
            and{" "}
            <strong className="text-amber">£48,000–£68,000 once qualified</strong>,
            with shift premiums and rest-day working pushing total earnings higher.
            Below: how to become a UK train driver, the psychometric assessments,
            eligibility, training, every recruiting operator, free resources and live
            trainee driver vacancies updated daily.
          </p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-2xl">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber font-semibold">Trainee</div>
              <div className="font-display text-2xl md:text-3xl font-bold mt-1">£27k–£33k</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber font-semibold">Qualified</div>
              <div className="font-display text-2xl md:text-3xl font-bold mt-1">£48k–£68k</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber font-semibold">Training</div>
              <div className="font-display text-2xl md:text-3xl font-bold mt-1">9–14m</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-amber font-semibold">Live driver jobs</div>
              <div className="font-display text-2xl md:text-3xl font-bold mt-1" data-testid="td-live">
                {vacancies.length || "—"}
              </div>
            </div>
          </div>

          <div className="mt-10 flex flex-wrap gap-3">
            <Button asChild size="lg" className="bg-amber text-ink hover:bg-lime-600 hover:text-ink">
              <Link to="/vacancies?role=train-driver">
                See live train driver jobs <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>

            <Button asChild size="lg" variant="outline" className="border-amber text-amber hover:bg-amber hover:text-ink">
              <Link to="/resources">Free train driver resources</Link>
            </Button>

            <Button asChild size="lg" variant="outline" className="border-amber text-amber hover:bg-amber hover:text-ink">
              <Link to="/depot-finder">Find driver depots near me</Link>
            </Button>
          </div>
        </div>
      </section>

      <nav className="border-b border-border bg-card sticky top-[60px] z-30 backdrop-blur-md">
        <div className="max-w-4xl mx-auto px-6 md:px-12 py-3 flex gap-x-6 gap-y-2 text-sm overflow-x-auto whitespace-nowrap">
          {SECTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`} className="text-muted-foreground hover:text-ink shrink-0">
              {s.h.replace(/\?$/, "")}
            </a>
          ))}
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-10">
        <OperatorSubscribeWidget operators={OPERATORS} />
      </div>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-16 space-y-16">
        {SECTIONS.map((s) => (
          <section key={s.id} id={s.id} className="scroll-mt-32">
            <h2 className="font-display font-bold text-ink text-3xl md:text-4xl tracking-tight">
              {s.h}
            </h2>

            {s.p?.map((p, i) => (
              <p
                key={i}
                className="text-muted-foreground leading-relaxed mt-4 text-[15px]"
                dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(p) }}
              />
            ))}

            {s.facts && (
              <dl className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 border border-border rounded-lg p-5 bg-card">
                {s.facts.map((f, i) => (
                  <div key={i}>
                    <dt className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-semibold">
                      {f.k}
                    </dt>
                    <dd className="font-mono text-sm text-ink mt-1">{f.v}</dd>
                  </div>
                ))}
              </dl>
            )}

            {s.list && (
              <ul className="mt-5 space-y-2">
                {s.list.map((item, i) => (
                  <li key={i} className="flex gap-3 text-[15px]">
                    <CheckCircle2 className="w-4 h-4 mt-1 text-amber-600 shrink-0" />
                    <span
                      className="text-muted-foreground"
                      dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(item) }}
                    />
                  </li>
                ))}
              </ul>
            )}

            {s.steps && (
              <ol className="mt-6 space-y-5">
                {s.steps.map((step) => (
                  <li key={step.n} className="grid md:grid-cols-[80px_1fr] gap-4 border-l-2 border-amber pl-5 md:pl-0 md:border-l-0">
                    <div className="font-mono text-amber-700 text-sm font-bold tracking-wider">
                      {step.n}
                    </div>
                    <div>
                      <div className="font-display font-bold text-ink text-lg">
                        {step.h}
                      </div>
                      <p className="text-muted-foreground text-[14px] leading-relaxed mt-1">
                        {step.p}
                      </p>
                    </div>
                  </li>
                ))}
              </ol>
            )}

            {s.id === "operators" && (
              <div className="mt-6 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                {[
                  "Avanti West Coast",
                  "LNER",
                  "GWR",
                  "ScotRail",
                  "Northern",
                  "TransPennine Express",
                  "CrossCountry",
                  "East Midlands Railway",
                  "Greater Anglia",
                  "Southeastern",
                  "SWR",
                  "c2c",
                  "Govia Thameslink Railway",
                  "Chiltern",
                  "Transport for Wales",
                  "Network Rail",
                  "DB Cargo",
                  "Freightliner",
                  "GB Railfreight",
                ].map((name) => (
                  <Link
                    key={name}
                    to={`/operators/${name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                    className="border border-border rounded-lg p-3 bg-card hover:border-amber transition-colors flex items-center gap-3"
                  >
                    <EmployerLogo name={name} size={28} className="shrink-0" />
                    <div className="text-sm font-semibold text-ink truncate">{name}</div>
                    <ArrowUpRight className="w-3.5 h-3.5 text-muted-foreground ml-auto shrink-0" />
                  </Link>
                ))}
              </div>
            )}
          </section>
        ))}

        <section id="live-vacancies" className="scroll-mt-32 bg-card border border-border rounded-lg p-8">
          <div className="text-[11px] uppercase tracking-[0.18em] text-ink font-semibold">
            Live driver vacancies
          </div>

          <h2 className="font-display font-bold text-ink text-3xl md:text-4xl tracking-tight mt-2">
            Current UK train driver jobs
          </h2>

          {vacancies.length === 0 ? (
            <p className="text-muted-foreground mt-4">
              No live trainee train driver vacancies right now. Subscribe to the{" "}
              <Link to="/" className="underline text-ink font-semibold">Monday Briefing</Link>{" "}
              to be notified when new ones are posted.
            </p>
          ) : (
            <div className="mt-6 space-y-3">
              {vacancies.map((v) => (
                <Link
                  key={v.id}
                  to={`/vacancies/${v.id}`}
                  className="block border border-border rounded-lg p-4 hover:border-amber transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <EmployerLogo name={v.employer_name} size={36} className="shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-ink line-clamp-1 group-hover:text-amber-700">
                        {v.title}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {v.employer_name} · {v.location}
                      </div>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-amber-700 shrink-0" />
                  </div>
                </Link>
              ))}
            </div>
          )}

          <div className="mt-6">
            <Button asChild className="bg-navy text-bone hover:bg-lime-600 hover:text-ink">
              <Link to="/vacancies?role=train-driver">
                View all train driver vacancies <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
          </div>
        </section>

        <section className="scroll-mt-32">
          <h2 className="font-display font-bold text-ink text-3xl md:text-4xl tracking-tight">
            Train driver jobs FAQ
          </h2>

          <div className="mt-6 space-y-6">
            {FAQS.map((f, i) => (
              <div key={i} className="border-t border-border pt-6">
                <h3 className="font-display font-bold text-ink text-xl tracking-tight">
                  {f.q}
                </h3>
                <p
                  className="text-muted-foreground leading-relaxed mt-2"
                  dangerouslySetInnerHTML={{ __html: renderInlineMarkdown(f.a) }}
                />
              </div>
            ))}
          </div>
        </section>

        <OperatorSubscribeWidget
          operators={OPERATORS}
          heading="Don't miss the next trainee driver vacancy"
          subheading="Trainee train driver roles get hundreds of applicants in the first 24 hours. Get our alert the moment a matching role drops — directly to your inbox, no scrolling job boards."
        />

        <section className="bg-navy text-bone rounded-lg p-8">
          <div className="text-[11px] uppercase tracking-[0.18em] text-amber font-semibold">
            Keep reading
          </div>

          <h2 className="font-display font-bold text-2xl md:text-3xl mt-1">
            Related UK rail career resources
          </h2>

          <p className="text-sm text-bone/70 mt-3 max-w-2xl">
            Use our free resources first, then move into structured preparation when you know which stage of the recruitment process you need to strengthen.
          </p>

          <div className="mt-6 grid md:grid-cols-3 gap-3">
            <Link to="/resources" className="block bg-bone/5 border border-amber/30 rounded-lg p-5 hover:bg-bone/10 transition-colors">
              <div className="font-display font-bold">Free rail career resources</div>
              <p className="text-sm text-bone/70 mt-2">
                Free guides, tools and explainers for applications, interviews, assessments and railway career planning.
              </p>
            </Link>

            <a
              href="https://www.traindriverfoundation.com/become-a-driver"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-bone/5 border border-amber/30 rounded-lg p-5 hover:bg-bone/10 transition-colors"
            >
              <div className="font-display font-bold">Train Driver Foundation guide</div>
              <p className="text-sm text-bone/70 mt-2">
                A detailed guide to the UK train driver recruitment process, including applications, CVs and interviews.
              </p>
            </a>

            <a
              href="https://traindriverpsychometrics.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
              className="block bg-bone/5 border border-amber/30 rounded-lg p-5 hover:bg-bone/10 transition-colors"
            >
              <div className="font-display font-bold">Psychometric test practice</div>
              <p className="text-sm text-bone/70 mt-2">
                Practice across key train driver assessment areas including concentration, vigilance, memory and reaction tests.
              </p>
            </a>
          </div>
        </section>
      </main>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(howToSchema) }} />
    </div>
  );
}